import { create } from "zustand";
import type { AnalysisSummary } from "./types";

/**
 * UI-facing import status tracked in the Zustand store.
 *
 * Progresses through: pending -> staging -> detecting -> analyzed -> ingesting
 * -> complete, with "error" reachable from any prior state.
 */
export type ImportStatus =
  | "pending"
  | "staging"
  | "detecting"
  | "sizeWarning"
  | "analyzed"
  | "ingesting"
  | "complete"
  | "error";

/**
 * In-memory representation of a single import for UI display and progress
 * tracking. Fresh uploads carry a `file` handle for retry support; resumed
 * imports (recovered from OPFS after a crash) do not.
 */
export type ImportItem = {
  id: string;
  fileName: string;
  /** Present only for fresh uploads, not for resumed imports. */
  file?: File;
  parentId: string;
  status: ImportStatus;
  progress: number;
  error?: string;
  completedAt?: number;
  objectId?: string;
  analysis?: AnalysisSummary;
  decompressedSize?: number;
};

type ImportStore = {
  imports: ImportItem[];
  /** Track a fresh upload that has an OS file handle. */
  add: (item: {
    id: string;
    fileName: string;
    file: File;
    parentId: string;
  }) => void;
  /** Track a resumed import recovered from OPFS (no file handle). */
  addResumed: (item: {
    id: string;
    fileName: string;
    parentId: string;
  }) => void;
  update: (id: string, update: Partial<ImportItem>) => void;
  remove: (id: string) => void;
  removeAllFailed: () => void;
};

export const useImportStore = create<ImportStore>((set) => ({
  imports: [],

  add({ id, fileName, file, parentId }) {
    set((state) => ({
      imports: [
        ...state.imports,
        { id, fileName, file, parentId, status: "pending", progress: 0 },
      ],
    }));
  },

  addResumed({ id, fileName, parentId }) {
    set((state) => ({
      imports: [
        ...state.imports,
        { id, fileName, parentId, status: "pending", progress: 0 },
      ],
    }));
  },

  update(id, patch) {
    set((state) => ({
      imports: state.imports.map((item) =>
        item.id === id ? { ...item, ...patch } : item,
      ),
    }));
  },

  remove(id) {
    set((state) => ({
      imports: state.imports.filter((item) => item.id !== id),
    }));
  },

  removeAllFailed() {
    set((state) => ({
      imports: state.imports.filter((item) => item.status !== "error"),
    }));
  },
}));
